import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { selectCurrentCoursePath } from '../Courses/selectors';
import { currentCourseBlockPagesByPathSelector } from '../Pages/selectors';

import './injectReducers';

import {
  modulesSelector,
  modulesByCoursePathSelector,
} from './selectors.common';

export {
  modulesSelector,
  modulesByCoursePathSelector,
  makeModulesSelectorByCoursePath,
} from './selectors.common';

export const currentCourseModulesSelector = createSelector(
  selectCurrentCoursePath,
  modulesSelector,
  (coursePath, modules) => {
    if (modules && modules.hasIn(['byCoursePath', coursePath])) {
      return modules
        .getIn(['byCoursePath', coursePath])
        .reduce(
          (modulesById, m) => modulesById.set(m.get('id'), m),
          Immutable.Map()
        );
    }
    return Immutable.Map();
  }
);

/*
Get all pages that are in modules, which module data added to the pages.

Output format:
  ImmutablePropTypes.listOf(
    page: ImmutablePropTypes.MapOf {
      path: PropTypes.string,
      module: ImmutablePropTypes.Map,
    }
  )
*/
export const currentCourseBlockPagesInModulesSelector = createSelector(
  currentCourseBlockPagesByPathSelector,
  currentCourseModulesSelector,
  (pages, modules) => {
    // Generate a list of pages which are within modules
    const pagesInModules =
      modules && pages
        ? modules.reduce((output, module) => {
            const pagesInModule = module
              .get('pages')
              .reduce((outputPages, page) => {
                const pageData = pages.get(page.get('path'));

                return pageData
                  ? outputPages.push(pageData.set('module', module))
                  : outputPages;
              }, Immutable.List());

            return output.concat(pagesInModule);
          }, Immutable.List())
        : Immutable.List();

    return pagesInModules;
  }
);

/*
Get a list of path of pages which are in modules, this is used in currentCourseStandAloneBlockPagesSelector

Output format:
  ImmutablePropTypes.listOf(
    pagePath: PropTypes.string,
  )
*/
export const currentCourseBlockPagePathsInModulesSelector = createSelector(
  currentCourseBlockPagesInModulesSelector,
  (pages) => {
    return pages ? pages.map((page) => page.get('path')) : Immutable.List();
  }
);

/*
Get a list of pages which are not in any module

Output format:
  ImmutablePropTypes.listOf(
    page: ImmutablePropTypes.MapOf {
      path: PropTypes.string,
    }
  )
*/
export const currentCourseStandAloneBlockPagesSelector = createSelector(
  currentCourseBlockPagesByPathSelector,
  currentCourseBlockPagePathsInModulesSelector,
  (pages, pathsInModules) => {
    const standAlonePage = pages
      ? pages.filter((page, path) => !pathsInModules.includes(path))
      : Immutable.OrderedMap();

    return standAlonePage.toList();
  }
);

/*
Get a map of all the pages in the course, which can be used to look up for page using page paths.

Output format:
ImmutablePropTypes.orderedMap(
  ImmutablePropTypes.listOf(
    page: ImmutablePropTypes.MapOf {
      path: PropTypes.string,
    }
  ),
  PropTypes.string,
)
*/
export const currentCourseBlockPagesSortedByModuleSelector = createSelector(
  currentCourseBlockPagesInModulesSelector,
  currentCourseStandAloneBlockPagesSelector,
  (pagesInModules, standAlonePages) => {
    /*
    1. Work out which pages are in module(s), output them in the order of the modules
    2. Work out which pages are outside of modules and add them into the output page list
    3. Merge 1. and 2. together
    4. Convert the merged list into the format below:

    {
      'courses/comp101/page_one': [
        {
          title: 'Page one'
          module: { title: 'module one', ... }
        },
      ]
      'courses/comp101/page_in_two_modules': [
        {
          title: 'Page in two modules'
          module: { title: 'module one', ... }
        },
        {
          title: 'Page in two modules'
          module: { title: 'module two', ... }
        }
      ]
      'courses/comp101/page_three': [
        {
          title: 'Page three'
          module: { title: 'module one', ... }
        },
      ]
    }

    One page can be in multiple modules, so the page instances will store in List.
    The pages are in order: Pages that are in modules first, and then the rest of the pages.
    */

    const allPages = pagesInModules.concat(standAlonePages);

    return allPages
      ? allPages.reduce(
          (output, page) =>
            output.set(
              page.get('path'),
              output.get(page.get('path'), Immutable.List()).push(page)
            ),
          Immutable.OrderedMap()
        )
      : Immutable.OrderedMap();
  }
);

// Returns a map of page ids -> modules
export const getModuleForPage = createSelector(
  currentCourseModulesSelector,
  (modules) => {
    return modules.reduce((acc, module) => {
      let modulePages = acc;

      module.get('pages').forEach((page) => {
        const pageId = page.get('id');

        if (!modulePages.has(pageId)) {
          modulePages = modulePages.set(pageId, module);
        }
      });

      return modulePages;
    }, Immutable.Map());
  }
);

export const selectCurrentCourseModulesArray = createSelector(
  [selectCurrentCoursePath, modulesByCoursePathSelector],
  (coursePath, modulesByCoursePath) => {
    return modulesByCoursePath.getIn([coursePath], Immutable.List());
  }
);

export const selectCurrentCourseDefaultModuleSet = createSelector(
  [selectCurrentCourseModulesArray],
  (modules) => {
    return modules.filter((m) => m.get('setName') === null);
  }
);

export const selectCurrentCourseModuleIds = createSelector(
  [selectCurrentCourseModulesArray],
  (modules) => {
    return modules.map((m) => m.get('id'));
  }
);
